import '../../styles/styles.css';
import React from 'react';
import StoryblokService from '../../lib/storyblok-service';

import Layout from '../../components/Layout';
import StoryblokComponents from '../../components/storyblok/StoryblokComponents.js';
import ArticleHeader from '../../components/ArticleHeader';
import Credits from '../../components/Credits';
import getFeaturedProjects from '../../lib/getFeaturedProjects';
import useEditableContent from '../../lib/hooks/useEditableContent';

const StoryblokArticleItem = ({
  story,
  date,
  authors,
  featuredProjectsList,
  preview,
}) => {
  const content = useEditableContent(story?.content, preview);

  return (
    <React.Fragment>
      {story && (
        <Layout
          metaData={{
            title: content.meta_title || story.name,
            description: content.meta_description,
            image: content.meta_image,
            type: 'article',
          }}
          featuredProjectsList={featuredProjectsList}
        >
          <article className="page page--article">
            <ArticleHeader
              component={{
                title: content.meta_title || story.name,
                section: content.section,
                date: date,
                readingTime: content.reading_time,
              }}
              className="g-row"
            />

            {content?.body.map((block) => (
              <StoryblokComponents block={block} key={block._uid} />
            ))}

            <Credits className="g-row" component={{ authors: authors }} />
          </article>
        </Layout>
      )}
    </React.Fragment>
  );
};

// This function gets called at build time on server-side.
export async function getStaticProps(ctx) {
  let { params, preview } = ctx;

  preview = preview || process.env.NODE_ENV !== 'production';

  const res = await StoryblokService.get(
    `cdn/stories/article/${params.storyblok}`,
    {
      version: preview ? 'draft' : 'published',
    }
  );

  const story = res.data.story;

  const rawDate =
    res.data.story.content.override_date ||
    res.data.story.first_published_at ||
    res.data.story.created_at;

  const date = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    timeZone: 'Europe/Amsterdam',
  }).format(new Date(rawDate));

  let authors = [];
  story.content.authors.tbody.forEach((author) => {
    authors.push({
      name: author.body[0] ? author.body[0].value : '',
      role: author.body[1] ? author.body[1].value : ''
    });
  });

  const featuredProjectsList = await getFeaturedProjects();

  return {
    props: { date, story, authors, featuredProjectsList, preview },
    revalidate: 600,
  };
}

export async function getStaticPaths() {
  const res = await StoryblokService.get(`cdn/stories/`, {
    starts_with: 'article/',
  });
  const articles = res.data.stories;

  const paths = articles.map((article) => ({
    params: { storyblok: article.slug },
  }));

  return {
    paths,
    fallback: true,
  };
}

export default StoryblokArticleItem;
